import { presentImportModal } from '@/components/ImporterModal';
import PermissionButton from '@/components_v2/ChakraComponents/PermissionButton';
import ExporterContextMenu from '@/components_v2/ExporterContextMenu';
import BaseModal from '@/components_v2/modals/BaseModal';
import FavoritesListModal from '@/components_v2/modals/FavoritesListModal';
import { useAuth } from '@/hooks/Authentication/auth';
import { useUserPermissions } from '@/hooks/Authentication/userPermissions';
import { useWorkspace } from '@/hooks/workspace';
import { taskServices as importService } from '@/modules/shared/adapters/ImporterService';
import { getExporter } from '@/screens/registrations/Tasks/services/getExporter';
import { colors } from '@/styles/theme';
import ModalStack from '@/utils/ModalStack';
import { Button, ButtonGroup, Flex, HStack, Icon } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiPlus, FiStar, FiX } from 'react-icons/fi';

import { useTasks } from '../../../../hooks/tasks';
import Filters from './components/Filters';
import Search from './components/Search';
import Stats from './components/Stats';
import TasksList from './components/TasksList';
import ButtonWithPermission from '@/components_v2/Buttons/ButtonWithPermission';

const ListView: React.FC = () => {
  const { openTaskForm, openTaskFormFromFavorite, createTasksFromFavorite } = useTasks();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspaceData } = useWorkspace();
  const { checkPermission } = useUserPermissions();
  const isAdmin = checkPermission('admin');

  const openImportModal = useCallback(() => {
    presentImportModal({
      importService,
    });
  }, []);

  const openFavorites = useCallback(() => {
    const modalKey = 'favorites-tasks-modal';
    ModalStack.push({
      key: modalKey,
      cancelable: true,
      children: (
        <BaseModal
          title={t('favorites.name', 'Favoritos')}
          HeaderContent={
            <div
              style={{ cursor: 'pointer', padding: 10 }}
              onClick={() => ModalStack.closeModal(modalKey)}
            >
              <FiX size={20} color={colors.lightWhite}/>
            </div>
          }
        >
          <FavoritesListModal
            favoriteType="task"
            onSelect={(favorite) => {
              ModalStack.closeModal(modalKey);
              if (openTaskFormFromFavorite) {
                openTaskFormFromFavorite(favorite);
              }
            }}
            onBatchCreate={isAdmin ? (favorite, count) => {
              ModalStack.closeModal(modalKey);
              createTasksFromFavorite(favorite, count);
            } : undefined}
          />
        </BaseModal>
      ),
    });
  }, [t, openTaskFormFromFavorite, createTasksFromFavorite]);

  const canCreateTask = user?.permit_create_task;

  return (
    <Flex overflow="hidden" direction="column" w="100%">
      <Flex
        mb={4}
        justifyContent="space-between"
        alignItems="center"
        flexDir="row"
      >
        <Search />
        <HStack>
          <ButtonGroup isAttached>
            <Button
              onClick={openImportModal}
              variant="outline"
              rightIcon={<Icon as={FiDownload} />}
            >
              {t('tasks.import_tasks')}
            </Button>
            <ExporterContextMenu
              getExporter={getExporter}
              options={{ only_public_events: false }}
              button={
                <Button variant="outline" rightIcon={<Icon as={FiDownload} />}>
                  {t('tasks.export_tasks')}
                </Button>
              }
              screenName="tasks"
            />

            <ExporterContextMenu
              getExporter={getExporter}
              options={{ only_public_events: false }}
              button={
                <Button variant="outline" rightIcon={<Icon as={FiDownload} />}>
                  {t('tasks.export_answers')}
                </Button>
              }
              screenName="task_answers"
            />

            {workspaceData.name === 'SESRJ' && (
              <ExporterContextMenu
                getExporter={getExporter}
                button={
                  <Button variant="outline" rightIcon={<Icon as={FiDownload} />}>
                    {t('tasks.export_flagged_and_works')}
                  </Button>
                }
                screenName="sesrj_task_answers"
              />
            )}
          </ButtonGroup>

          <PermissionButton
            rightIcon={<Icon as={FiStar} />}
            colorScheme="gray"
            onClick={openFavorites}
          >
            {t('favorites.show_favorites', 'Favoritos')}
          </PermissionButton>

          <PermissionButton
            rightIcon={<Icon as={FiPlus} />}
            colorScheme="brand"
            onClick={() => openTaskForm()}
            externalPermission={canCreateTask}
          >
            {t('tasks.screen_header.new_task')}
          </PermissionButton>
        </HStack>
      </Flex>
      {workspaceData.name !== 'foco' && <Stats />}
      <Filters />
      <TasksList />
    </Flex>
  );
};

export default ListView;
